import { useEffect, useRef, useState } from "react";
import { Globe, X, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";

const SYNC_STYLE = {
  synced: { bg: "#DCFCE7", fg: "#15803D" },
  pending: { bg: "#FEF9C3", fg: "#A16207" },
  error: { bg: "#FEE2E2", fg: "#B91C1C" },
  not_linked: { bg: "var(--surface-alt)", fg: "var(--text-muted)" },
};

export function SyncBadge({ status }) {
  const { t } = useI18n();
  const s = SYNC_STYLE[status || "not_linked"] || SYNC_STYLE.not_linked;
  return (
    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.fg }}>
      {t(`sync.${status || "not_linked"}`)}
    </span>
  );
}

export function ProvidersAdmin() {
  const { t, L } = useI18n();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ wordpress_site_url: "", directorist_listing_id: "", public_profile_url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/providers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openMapping = (p) => {
    setForm({
      wordpress_site_url: p.wordpress_site_url || "",
      directorist_listing_id: p.directorist_listing_id || "",
      public_profile_url: p.public_profile_url || "",
    });
    setEditing(p);
  };
  const close = () => setEditing(null);

  const saveMapping = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/providers/${editing.id}/mapping`, form);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const markSynced = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/providers/${editing.id}/mark-synced`);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;

  return (
    <div className="space-y-2" data-testid="providers-admin">
      {items.map((p) => (
        <Card key={p.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`provider-admin-${p.id}`}>
          <div className="h-10 w-10 shrink-0 overflow-hidden" style={{ background: "var(--surface-alt)", borderRadius: "var(--radius)" }}>
            {p.cover_image && <img src={p.cover_image} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate" style={{ color: "var(--text)" }}>{p.business_name}</div>
            <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{p.categories?.map((c) => L(c.name)).join(", ")} · {L(p.location_name)}</div>
          </div>
          <StatusBadge status={p.status} />
          {p.is_demo && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap" title="Synthetische Demo-Daten – nicht echt" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
              Demo Anbieter – nicht echt
            </span>
          )}
          <SyncBadge status={p.sync_status} />
          <Btn variant="soft" onClick={() => openMapping(p)} className="h-9 px-3 text-xs" data-testid={`provider-mapping-${p.id}`}>
            <Globe className="h-3.5 w-3.5" /> WordPress
          </Btn>
        </Card>
      ))}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="mapping-modal">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Globe className="h-5 w-5" style={{ color: "var(--accent)" }} /> {t("map.title")}</h3>
              <button onClick={close} data-testid="mapping-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm" style={{ color: "var(--text-muted)" }}>{editing.business_name}</span>
              <SyncBadge status={editing.sync_status} />
              {editing.last_sync_at && <span className="text-xs" style={{ color: "var(--text-muted)" }}>· {t("map.lastSync")}: {new Date(editing.last_sync_at).toLocaleDateString()}</span>}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("map.wpUrl")}</label>
                <input data-testid="map-wpurl-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.wordpress_site_url} onChange={(e) => setForm({ ...form, wordpress_site_url: e.target.value })} placeholder="https://www.elenya.ch" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("map.listingId")}</label>
                <input data-testid="map-listingid-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.directorist_listing_id} onChange={(e) => setForm({ ...form, directorist_listing_id: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("map.publicUrl")}</label>
                <input data-testid="map-publicurl-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.public_profile_url} onChange={(e) => setForm({ ...form, public_profile_url: e.target.value })} />
                {form.public_profile_url && (
                  <a href={form.public_profile_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: "var(--accent)" }}>
                    <ExternalLink className="h-3 w-3" /> {form.public_profile_url}
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Btn onClick={saveMapping} disabled={busy} className="flex-1" data-testid="map-save-btn">{t("common.save")}</Btn>
                <Btn variant="accent" onClick={markSynced} disabled={busy} data-testid="map-marksynced-btn"><CheckCircle2 className="h-4 w-4" /> {t("map.markSynced")}</Btn>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
